import { useQuery } from "@tanstack/react-query";
import { api } from "./api";

/**
 * Race Center hooks — pacing / nutrition / gear.
 *
 * All three endpoints are stateless; we rely on React Query's
 * staleTime to keep them cached without re-rolling math on every
 * tab switch.
 */

export interface PacingSplit {
  index: number;
  distance_km: number;
  cumulative_km: number;
  elapsed_seconds: number;
  segment_seconds: number;
  target_pace_per_km_s: number | null;
  target_power_watts: number | null;
  hr_low: number;
  hr_high: number;
  grade_pct: number;
  note: string | null;
}

export interface PacingBody {
  discipline: "run" | "trail" | "cycle" | "swim";
  distance_km: number;
  target_time_seconds: number;
  course_profile?: { distance_km: number; elevation_gain_m: number; elevation_loss_m: number }[];
  cp_watts?: number;
  pace_zone?: string;
  intensity_factor?: number;
  max_hr?: number;
}

export interface PacingResp {
  discipline: string;
  splits: PacingSplit[];
}

export function usePacing(body: PacingBody, enabled = true) {
  return useQuery<PacingResp>({
    queryKey: [
      "race-pacing",
      body.discipline,
      body.distance_km,
      body.target_time_seconds,
      body.cp_watts ?? null,
      body.pace_zone ?? null,
    ],
    queryFn: async () => {
      const r = await api.post<PacingResp>("/athlete/race/pacing", body);
      return r.data;
    },
    enabled,
    staleTime: 10 * 60_000,
  });
}

export interface FuelingSlot {
  minutes_from_start: number;
  duration_minutes: number;
  carb_g: number;
  fluid_ml: number;
  sodium_mg: number;
}

export interface PreRacePlan {
  carb_g: number;
  fluid_ml: number;
  last_meal_minutes_before: number;
  note: string;
}

export interface NutritionBody {
  duration_minutes: number;
  intensity?: "easy" | "moderate" | "hard" | "race";
  hot_weather?: boolean;
  body_weight_kg?: number;
  race_distance_km?: number;
}

export interface CaffeinePlan {
  applicable: boolean;
  dose_mg: number | null;
  mg_per_kg: number | null;
  espresso_cups: number | null;
  timing_min_before_low: number;
  timing_min_before_high: number;
  top_up: boolean;
}

export interface NutritionResp {
  pre_race: PreRacePlan | null;
  in_race: FuelingSlot[];
  totals: { carb_g: number; fluid_ml: number; sodium_mg: number };
  caffeine: CaffeinePlan | null;
}

export function useNutrition(body: NutritionBody, enabled = true) {
  return useQuery<NutritionResp>({
    queryKey: ["race-nutrition", body.duration_minutes, body.intensity, body.hot_weather, body.body_weight_kg, body.race_distance_km],
    queryFn: async () => {
      const r = await api.post<NutritionResp>("/athlete/race/nutrition", body);
      return r.data;
    },
    enabled,
    staleTime: 10 * 60_000,
  });
}

export interface GearItem {
  key: string;
  mandatory: boolean;
  note: string | null;
}

export interface GearCategory {
  key: string;
  items: GearItem[];
}

export interface GearResp {
  discipline: string;
  race_format: string | null;
  categories: GearCategory[];
  mandatory_count: number;
}

export interface GearQuery {
  discipline: string;
  race_format?: string | null;
  weather_hint?: "hot" | "cold" | "rain" | null;
}

export function useGear(q: GearQuery, enabled = true) {
  return useQuery<GearResp>({
    queryKey: ["race-gear", q.discipline, q.race_format ?? null, q.weather_hint ?? null],
    queryFn: async () => {
      const params: Record<string, string> = { discipline: q.discipline };
      if (q.race_format) params.race_format = q.race_format;
      if (q.weather_hint) params.weather_hint = q.weather_hint;
      const r = await api.get<GearResp>("/athlete/race/gear", { params });
      return r.data;
    },
    enabled,
    staleTime: 30 * 60_000,
  });
}

// ============================================================
// P0.1 R9 — live race mode (athlete-side current workout poll)
// ============================================================

export interface CurrentWorkout {
  is_in_workout: boolean;
  workout_id: number | null;
  sport: string | null;
  title: string | null;
  started_at: string | null;
  elapsed_min: number | null;
  planned_duration_minutes: number | null;
  zone_hint: string | null;
}

/**
 * useCurrentWorkout — poll in-progress workout every 10s for live race HUD.
 *
 * Polling stops when the tab is backgrounded (React Query default
 * `refetchIntervalInBackground=false`). `enabled` flag lets callers gate
 * fetch on race-day proximity so the request doesn't fire outside the
 * relevant window.
 */
export function useCurrentWorkout(enabled = true) {
  return useQuery<CurrentWorkout>({
    queryKey: ["athlete-current-workout"],
    queryFn: async () => {
      const r = await api.get<CurrentWorkout>("/athlete/workouts/current");
      return r.data;
    },
    enabled,
    refetchInterval: 10_000,
    staleTime: 5_000,
  });
}
